<template>
  <!-- 원형 프로필 크롭퍼: 300×300 정사각 무대 + 원형 마스크. 드래그로 위치, 슬라이더로 확대. -->
  <div class="cropper">
    <div class="cropper__stage" @mousedown="onDown" @touchstart.prevent="onDown">
      <img
        v-if="src"
        :src="src"
        ref="img"
        class="cropper__img"
        :style="imgStyle"
        draggable="false"
        @load="onImgLoad"
      />
      <div class="cropper__ring"></div>
    </div>

    <div class="cropper__zoom">
      <v-icon size="17" color="#9b93b8">mdi-image-size-select-small</v-icon>
      <v-slider
        v-model="zoom"
        :min="1"
        :max="3"
        :step="0.01"
        hide-details
        dense
        color="#7c4dff"
        track-color="#e7e1f5"
        class="mx-2"
      ></v-slider>
      <v-icon size="21" color="#9b93b8">mdi-image-size-select-large</v-icon>
    </div>
  </div>
</template>

<script>
const SIZE = 300;

export default {
  name: "ImageCircleCropper",
  props: {
    src: { type: String, default: null },
  },
  data: () => ({
    natW: 0,
    natH: 0,
    baseScale: 1,
    zoom: 1,
    offsetX: 0,
    offsetY: 0,
    _drag: null,
  }),
  computed: {
    scale() {
      return this.baseScale * this.zoom;
    },
    dispW() {
      return this.natW * this.scale;
    },
    dispH() {
      return this.natH * this.scale;
    },
    imgStyle() {
      return {
        width: `${this.dispW}px`,
        height: `${this.dispH}px`,
        transform: `translate(${this.offsetX}px, ${this.offsetY}px)`,
      };
    },
  },
  watch: {
    // 줌은 뷰포트 중심을 기준으로 (중심 고정 확대/축소)
    zoom(nv, ov) {
      const oldScale = this.baseScale * ov;
      const newScale = this.baseScale * nv;
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const imgX = (cx - this.offsetX) / oldScale;
      const imgY = (cy - this.offsetY) / oldScale;
      this.offsetX = cx - imgX * newScale;
      this.offsetY = cy - imgY * newScale;
      this._clamp();
    },
  },
  beforeDestroy() {
    this._unbind();
  },
  methods: {
    onImgLoad(e) {
      const img = e.target;
      this.natW = img.naturalWidth || 1;
      this.natH = img.naturalHeight || 1;
      this.baseScale = SIZE / Math.min(this.natW, this.natH); // 짧은 변이 무대를 꽉 채움(cover)
      this.zoom = 1;
      this.offsetX = (SIZE - this.dispW) / 2;
      this.offsetY = (SIZE - this.dispH) / 2;
    },
    onDown(ev) {
      const p = this._point(ev);
      this._drag = { x: p.x, y: p.y, ox: this.offsetX, oy: this.offsetY };
      window.addEventListener("mousemove", this.onMove);
      window.addEventListener("mouseup", this._unbind);
      window.addEventListener("touchmove", this.onMove, { passive: false });
      window.addEventListener("touchend", this._unbind);
    },
    onMove(ev) {
      if (!this._drag) return;
      if (ev.cancelable) ev.preventDefault();
      const p = this._point(ev);
      this.offsetX = this._drag.ox + (p.x - this._drag.x);
      this.offsetY = this._drag.oy + (p.y - this._drag.y);
      this._clamp();
    },
    _unbind() {
      this._drag = null;
      window.removeEventListener("mousemove", this.onMove);
      window.removeEventListener("mouseup", this._unbind);
      window.removeEventListener("touchmove", this.onMove);
      window.removeEventListener("touchend", this._unbind);
    },
    _point(ev) {
      const t = ev.touches && ev.touches[0];
      return { x: t ? t.clientX : ev.clientX, y: t ? t.clientY : ev.clientY };
    },
    // 이미지가 항상 무대를 덮도록 offset 제한
    _clamp() {
      this.offsetX = Math.min(0, Math.max(SIZE - this.dispW, this.offsetX));
      this.offsetY = Math.min(0, Math.max(SIZE - this.dispH, this.offsetY));
    },
    // 보이는 정사각 영역을 300×300 PNG Blob으로 반환 (원형은 표시상 마스크 — 저장은 정사각, CSS가 원으로 표시)
    getBlob() {
      return new Promise((resolve, reject) => {
        const img = this.$refs.img;
        if (!img) return reject(new Error("E_NO_IMAGE"));
        const canvas = document.createElement("canvas");
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, this.offsetX, this.offsetY, this.dispW, this.dispH);
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("E_CROP_FAILED"))), "image/png");
      });
    },
  },
};
</script>

<style scoped>
.cropper {
  user-select: none;
}
.cropper__stage {
  position: relative;
  width: 300px;
  height: 300px;
  max-width: 100%;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  background: #efe9f6;
  cursor: grab;
  touch-action: none;
}
.cropper__stage:active {
  cursor: grabbing;
}
.cropper__img {
  position: absolute;
  top: 0;
  left: 0;
  max-width: none;
  will-change: transform;
  -webkit-user-drag: none;
}
/* 원형 마스크: 원 밖을 옅게 덮고 원 테두리 표시 (드래그 통과) */
.cropper__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(255, 255, 255, 0.72);
  border: 2px solid rgba(124, 77, 255, 0.9);
  pointer-events: none;
}
.cropper__zoom {
  display: flex;
  align-items: center;
  max-width: 300px;
  margin: 12px auto 0;
}
</style>
